import { Badge, Box, Tooltip } from "@mui/material";
import PendingActionsOutlinedIcon from "@mui/icons-material/PendingActionsOutlined";
import { useRef } from "react";
import { BackgroundActivityFlyout, BACKGROUND_ACTIVITY_FLYOUT_ID } from "./BackgroundActivityFlyout";
import { useBackgroundActivity } from "./useBackgroundActivity";

const WHITE = "#fff";
const WHITE_72 = "rgba(255,255,255,0.72)";
const WHITE_55 = "rgba(255,255,255,0.55)";
const WHITE_ACTIVE_BG = "rgba(255,255,255,0.16)";
const WHITE_HOVER_BG = "rgba(255,255,255,0.09)";

const tooltipSlotProps = {
    tooltip: {
        sx: {
            bgcolor: "common.black",
            color: "common.white",
            fontSize: "0.75rem",
            fontWeight: 500,
            letterSpacing: "0.025em",
            borderRadius: 1
        }
    }
} as const;

interface BackgroundActivityNavButtonProps {
    expanded: boolean;
}

export function BackgroundActivityNavButton({ expanded }: BackgroundActivityNavButtonProps) {
    const { activeCount, runningCount, queuedCount, isOpen, toggle } = useBackgroundActivity();
    const buttonRef = useRef<HTMLButtonElement>(null);
    const label = activeCount > 0 ? `Background activity, ${activeCount} active ${activeCount === 1 ? "job" : "jobs"}` : "Background activity";

    const button = (
        <button
            ref={buttonRef}
            type="button"
            aria-label={label}
            aria-haspopup="dialog"
            aria-expanded={isOpen}
            aria-controls={isOpen ? BACKGROUND_ACTIVITY_FLYOUT_ID : undefined}
            data-ui-bridge="nav.background-activity"
            data-ui-label="Background activity"
            data-ui-role="button"
            data-ui-group="navigation"
            data-ui-description="View product and Work Product processing jobs"
            onClick={toggle}
            style={{
                display: "flex",
                alignItems: "center",
                justifyContent: expanded ? "flex-start" : "center",
                gap: 11,
                width: expanded ? "100%" : 38,
                height: expanded ? "auto" : 38,
                minHeight: 38,
                padding: expanded ? "8px 10px" : 0,
                border: "none",
                borderRadius: 10,
                cursor: "pointer",
                background: isOpen ? WHITE_ACTIVE_BG : "transparent",
                color: WHITE_72,
                textAlign: "left",
                fontFamily: "inherit",
                transition: "background .12s, color .12s"
            }}
            onMouseEnter={event => {
                if (!isOpen) event.currentTarget.style.background = WHITE_HOVER_BG;
                event.currentTarget.style.color = WHITE;
            }}
            onMouseLeave={event => {
                if (!isOpen) event.currentTarget.style.background = "transparent";
                event.currentTarget.style.color = WHITE_72;
            }}
        >
            <Badge
                badgeContent={activeCount}
                max={99}
                invisible={activeCount === 0}
                overlap="circular"
                sx={{
                    flex: "0 0 auto",
                    "& .MuiBadge-badge": {
                        minWidth: 16,
                        height: 16,
                        px: 0.35,
                        border: "1.5px solid #102B72",
                        bgcolor: "#6574F7",
                        color: WHITE,
                        fontSize: 9,
                        fontWeight: 800,
                        animation: runningCount > 0 ? "background-activity-pulse 1.8s ease-out infinite" : "none"
                    },
                    "@keyframes background-activity-pulse": {
                        "0%, 55%": { boxShadow: "0 0 0 0 rgba(101,116,247,0.55)" },
                        "100%": { boxShadow: "0 0 0 6px rgba(101,116,247,0)" }
                    },
                    "@media (prefers-reduced-motion: reduce)": {
                        "& .MuiBadge-badge": { animation: "none" }
                    }
                }}
            >
                <PendingActionsOutlinedIcon sx={{ fontSize: 19 }} />
            </Badge>

            {expanded && (
                <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: "block", color: WHITE, fontSize: 13.5, fontWeight: isOpen ? 650 : 500, whiteSpace: "nowrap" }}>
                        Background activity
                    </span>
                    <span style={{ display: "block", color: WHITE_55, fontSize: 11, lineHeight: 1.25, whiteSpace: "nowrap" }}>
                        {activeCount > 0 ? `${runningCount} running · ${queuedCount} queued` : "No active jobs"}
                    </span>
                </span>
            )}
        </button>
    );

    return (
        <Box
            sx={{
                display: "flex",
                justifyContent: expanded ? "stretch" : "center",
                px: expanded ? 1.25 : 0,
                py: 0.75
            }}
        >
            {expanded ? (
                button
            ) : (
                <Tooltip title="Background activity" placement="right" enterDelay={300} slotProps={tooltipSlotProps}>
                    {button}
                </Tooltip>
            )}
            <BackgroundActivityFlyout anchorEl={buttonRef.current} />
        </Box>
    );
}
